// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import L from 'leaflet';
import type { AppContext } from '../app';
import { el } from '../dom';
import { formatMass, formatYear } from '../format';
import { divisionColor } from '../colors';

export function renderMap(root: HTMLElement, ctx: AppContext) {
  root.appendChild(el('h1', { class: 'section-title' }, 'National emissions map'));
  root.appendChild(el('p', { class: 'section-sub' }, [
    'Every reporting facility, sized by lifetime emissions and coloured by industry division. Click a facility for its full record.'
  ]));

  // Controls: medium toggle, year filter, top-N filter
  const yearOptions = ['any', 'latest', '5y'] as const;
  let medium: 'all' | 'air' | 'water' | 'land' = 'all';
  let yearMode: typeof yearOptions[number] = 'latest';
  let topN = 1500;

  const controls = el('div', { class: 'control-row' });
  const mediumGroup = el('div', { class: 'btn-group' }, [
    btnGroupItem('All', () => setMedium('all'), true),
    btnGroupItem('Air', () => setMedium('air')),
    btnGroupItem('Water', () => setMedium('water')),
    btnGroupItem('Land', () => setMedium('land'))
  ]);
  const yearGroup = el('div', { class: 'btn-group' }, [
    btnGroupItem('Latest year', () => setYear('latest'), true),
    btnGroupItem('Last 5 yrs', () => setYear('5y')),
    btnGroupItem('All time', () => setYear('any'))
  ]);
  const limitGroup = el('div', { class: 'btn-group' }, [
    btnGroupItem('Top 500', () => setLimit(500)),
    btnGroupItem('Top 1500', () => setLimit(1500), true),
    btnGroupItem('All', () => setLimit(20000))
  ]);

  controls.appendChild(el('div', { class: 'field' }, [el('label', null, 'Medium'), mediumGroup]));
  controls.appendChild(el('div', { class: 'field' }, [el('label', null, 'Reporting'), yearGroup]));
  controls.appendChild(el('div', { class: 'field' }, [el('label', null, 'Sites shown'), limitGroup]));
  controls.appendChild(legend());
  root.appendChild(controls);

  const mapWrap = el('div', { class: 'map-container' });
  root.appendChild(mapWrap);

  const map = L.map(mapWrap, {
    center: [-26.5, 134],
    zoom: 4,
    scrollWheelZoom: true,
    preferCanvas: true
  });
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    attribution: '© OpenStreetMap contributors, © CARTO',
    maxZoom: 18
  }).addTo(map);

  const layer = L.layerGroup().addTo(map);
  draw();

  // Element only gets real dimensions after first paint; nudge Leaflet to re-measure.
  requestAnimationFrame(() => map.invalidateSize());
  setTimeout(() => map.invalidateSize(), 250);

  function setMedium(m: typeof medium) {
    medium = m;
    setActive(mediumGroup, m === 'all' ? 'All' : m[0].toUpperCase() + m.slice(1));
    draw();
  }
  function setYear(y: typeof yearMode) {
    yearMode = y;
    setActive(yearGroup, y === 'latest' ? 'Latest year' : y === '5y' ? 'Last 5 yrs' : 'All time');
    draw();
  }
  function setLimit(n: number) {
    topN = n;
    setActive(limitGroup, n === 500 ? 'Top 500' : n === 1500 ? 'Top 1500' : 'All');
    draw();
  }

  function draw() {
    layer.clearLayers();
    const latest = ctx.data.meta.latest_year;
    const filtered = ctx.data.facilities.filter(f => {
      if (f.lat == null || f.lng == null) return false;
      if (yearMode === 'latest' && f.latest_year !== latest) return false;
      if (yearMode === '5y' && (f.latest_year ?? 0) < latest - 4) return false;
      return true;
    });
    filtered.sort((a, b) => valueFor(b) - valueFor(a));
    const slice = filtered.slice(0, topN);
    const maxVal = slice[0] ? valueFor(slice[0]) : 1;

    for (const f of slice) {
      const value = valueFor(f);
      if (value <= 0) continue;
      const radius = Math.max(4, Math.min(28, Math.sqrt(value / maxVal) * 28));
      const color = divisionColor(f.division_code);
      const marker = L.circleMarker([f.lat!, f.lng!], {
        radius,
        weight: 1,
        color: '#fff',
        fillColor: color,
        fillOpacity: 0.75
      });
      const popupHtml = `
        <div class="popup-name">${escapeHtml(f.name)}</div>
        <div class="popup-sub">${escapeHtml(f.anzsic_class_name)}</div>
        <div class="popup-stat"><span>Air</span><span>${formatMass(f.air)}</span></div>
        <div class="popup-stat"><span>Water</span><span>${formatMass(f.water)}</span></div>
        <div class="popup-stat"><span>Land</span><span>${formatMass(f.land)}</span></div>
        <div class="popup-stat" style="margin-top:6px; border-top:1px solid var(--border-subtle); padding-top:4px;"><strong>Total</strong><strong>${formatMass(f.total)}</strong></div>
        <a class="popup-action" href="#facility?id=${f.id}">Open detail →</a>
      `;
      marker.bindPopup(popupHtml, { className: 'facility-popup', maxWidth: 320 });
      marker.bindTooltip(`${f.name} · ${medium === 'all' ? 'total' : medium} ${formatMass(value)}`, { direction: 'top' });
      marker.addTo(layer);
    }
  }

  function valueFor(f: import('../types').Facility): number {
    if (medium === 'all') return f.total;
    if (medium === 'air') return f.air;
    if (medium === 'water') return f.water;
    if (medium === 'land') return f.land;
    return 0;
  }
}

function btnGroupItem(label: string, onclick: () => void, active = false): HTMLElement {
  return el('button', { class: 'btn' + (active ? ' active' : ''), onclick, 'data-label': label }, label);
}

function setActive(group: HTMLElement, label: string) {
  for (const btn of Array.from(group.querySelectorAll('.btn'))) {
    btn.classList.toggle('active', btn.getAttribute('data-label') === label);
  }
}

function legend(): HTMLElement {
  return el('div', { class: 'legend' }, [
    swatch('A', 'Agriculture'),
    swatch('B', 'Mining'),
    swatch('C', 'Manufacturing'),
    swatch('D', 'Electricity/Gas/Water'),
    swatch('I', 'Transport'),
    swatch('E', 'Construction'),
    swatch('H', 'Hospitality')
  ]);
}
function swatch(code: string, label: string): HTMLElement {
  return el('span', null, [
    el('span', { class: 'legend-swatch', style: `background:${divisionColor(code)}` }),
    label
  ]);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

// Used in popup link
void formatYear;
